import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import dropdownIcon from '../assets/icons/Dropdown.svg';
import {
  createFieldOfficer,
  fetchDofficers,
  humanizeError,
  mapUserToFieldOfficerRow,
  normalizeUserDuplicateMessage,
  setUserActive,
} from '../services/api';
import ClientProjectDropdowns from '../components/filters/ClientProjectDropdowns';
import HierarchyFilterDropdowns from '../components/filters/HierarchyFilterDropdowns';
import Button from '../components/global/Button';
import DataTable from '../components/global/DataTable';
import exportIcon from '../assets/icons/ExportIcon.svg';
import { applyHierarchyFilters } from '../constants/hierarchyFilters';
import { useAuth } from '../context/AuthContext';
import { useClientProjectScope } from '../hooks/useClientProjectScope';
import { useHierarchyFilters } from '../hooks/useHierarchyFilters';
import { getAllProjectOptions } from '../utils/clientProjectSelectOptions';
import { buildPageList } from '../utils/pagination';
import './FieldOfficers.css';
import './Dashboard.css';
import './StatusMetersPage.css';

const PAGE_SIZE = 10;

export default function FieldOfficers() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [localSearch, setLocalSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [form, setForm] = useState({
    username: '',
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    phone: '',
    project_ids: [],
  });
  const [projectMenuOpen, setProjectMenuOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [togglingId, setTogglingId] = useState(null);
  const projectPickerRef = useRef(null);

  const { user } = useAuth();
  const scope = useClientProjectScope();
  const hierarchy = useHierarchyFilters(rows);

  const projectOptionsForAssign = useMemo(() => getAllProjectOptions(user), [user]);

  const visibleProjectOptions = useMemo(() => {
    const q = projectSearch.trim().toLowerCase();
    if (!q) return projectOptionsForAssign;
    return projectOptionsForAssign.filter((p) => p.label.toLowerCase().includes(q));
  }, [projectOptionsForAssign, projectSearch]);

  const selectedProjectLabel = useMemo(() => {
    if (!form.project_ids.length) return '';
    const byId = new Map(projectOptionsForAssign.map((p) => [String(p.id), p.label]));
    return form.project_ids
      .map((id) => byId.get(String(id)) || `Project ${id}`)
      .join(', ');
  }, [form.project_ids, projectOptionsForAssign]);

  useEffect(() => {
    if (!projectMenuOpen) return undefined;
    const onPointerDown = (event) => {
      if (!projectPickerRef.current) return;
      if (!projectPickerRef.current.contains(event.target)) {
        setProjectMenuOpen(false);
      }
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setProjectMenuOpen(false);
    };
    window.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [projectMenuOpen]);

  const toggleProject = (id) => {
    const key = String(id);
    setForm((prev) => {
      const exists = prev.project_ids.some((pid) => String(pid) === key);
      const next = exists
        ? prev.project_ids.filter((pid) => String(pid) !== key)
        : [...prev.project_ids, key];
      return { ...prev, project_ids: next };
    });
  };

  const clearProjects = () => {
    setForm((prev) => ({ ...prev, project_ids: [] }));
  };

  const filteredRows = useMemo(() => {
    let data = applyHierarchyFilters(rows, hierarchy.filterByField, hierarchy.locationFilter);
    const q = localSearch.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (row) =>
        String(row.name || '').toLowerCase().includes(q) ||
        String(row.contact || '').toLowerCase().includes(q) ||
        String(row.status || '').toLowerCase().includes(q),
    );
  }, [rows, hierarchy.filterByField, hierarchy.locationFilter, localSearch]);

  useEffect(() => {
    setPage(1);
  }, [localSearch, hierarchy.filterByField, hierarchy.locationFilter, scope.selectedClientId, scope.selectedProjectId]);

  const loadData = useCallback(async ({ force = false, showLoading = true } = {}) => {
    if (showLoading) setLoading(true);
    setError('');
    try {
      const projectId = scope.selectedProjectId || undefined;
      const data = await fetchDofficers({ force, projectId });
      setRows(data);
    } catch (err) {
      setError(humanizeError(err, 'Could not load field officers. Please try again.'));
    } finally {
      if (showLoading) setLoading(false);
    }
  }, [scope.selectedProjectId]);

  const handleToggleActive = useCallback(
    async (row) => {
      const isActive = String(row.status).toLowerCase() === 'active';
      const nextActive = !isActive;
      const action = nextActive ? 'activate' : 'deactivate';

      if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${row.name}?`)) {
        return;
      }

      setTogglingId(row.id);
      setError('');
      try {
        await setUserActive(row.id, nextActive);
        await loadData({ force: true, showLoading: false });
      } catch (err) {
        setError(
          humanizeError(err, `Could not ${action} the field officer. Please try again.`),
        );
      } finally {
        setTogglingId(null);
      }
    },
    [loadData],
  );

  useEffect(() => {
    loadData();
  }, [loadData]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const slOffset = (safePage - 1) * PAGE_SIZE;
  const pageRows = useMemo(
    () => filteredRows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredRows, safePage]
  );
  const pageList = useMemo(() => buildPageList(safePage, totalPages), [safePage, totalPages]);

  const foColumns = useMemo(
    () => [
      {
        id: 'sl',
        header: 'Sl. No',
        cell: (_, idx) => String(slOffset + idx + 1),
      },
      { id: 'name', header: 'Name', cell: (r) => r.name },
      { id: 'contact', header: 'Contact', cell: (r) => r.contact },
      {
        id: 'activity',
        header: 'Activity',
        cell: (r) => (
          <>
            <span className="foActivity">
              {r.installed} / <span className="foAccept">{r.accepted}</span> /{' '}
              <span className="foReject">{r.rejected}</span>
            </span>
            <div className="foActivitySub">Inst/Acc/Rej</div>
          </>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: (r) => (
          <span
            className={`foStatus ${String(r.status).toLowerCase() === 'active' ? 'active' : 'inactive'}`}
          >
            {r.status}
          </span>
        ),
      },
      {
        id: 'action',
        header: 'Action',
        cell: (r) => (
          <button
            className="foLinkBtn"
            type="button"
            disabled={togglingId === r.id}
            onClick={() => handleToggleActive(r)}
          >
            {togglingId === r.id
              ? 'Saving...'
              : String(r.status).toLowerCase() === 'active'
                ? 'Deactivate'
                : 'Activate'}
          </button>
        ),
      },
    ],
    [slOffset, togglingId, handleToggleActive],
  );

  const exportCsv = () => {
    const headers = [
      'Sl. No',
      'Name',
      'Contact',
      'Installed',
      'Accepted',
      'Rejected',
      'Status',
    ];
    const csvRows = filteredRows.map((row, index) => [
      index + 1,
      row.name,
      row.contact,
      row.installed,
      row.accepted,
      row.rejected,
      row.status,
    ]);
    const csv = [headers, ...csvRows].map((line) => line.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'field_officers.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setForm({
      username: '',
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      phone: '',
      project_ids: [],
    });
    setProjectMenuOpen(false);
    setProjectSearch('');
    setSubmitError('');
    setSubmitSuccess('');
  };

  const normalizeProjectIds = (ids) =>
    ids
      .map((item) => Number(item))
      .filter((item) => Number.isInteger(item) && item > 0);

  const handleCreateFieldOfficer = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setSubmitError('');
    setSubmitSuccess('');

    try {
      const projectIds = normalizeProjectIds(form.project_ids);
      if (projectIds.length === 0) {
        throw new Error('Select at least one project to assign.');
      }

      const created = await createFieldOfficer({
        username: form.username.trim(),
        email: form.email.trim(),
        password: form.password,
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        phone: form.phone.trim(),
        project_ids: projectIds,
      });

      const createdUser = created?.data;
      hierarchy.resetHierarchy();

      const newRow = mapUserToFieldOfficerRow(createdUser);
      if (newRow) {
        setRows((prev) => {
          if (prev.some((r) => Number(r.id) === Number(newRow.id))) return prev;
          return [newRow, ...prev];
        });
      }

      try {
        const refreshProjectId = scope.selectedProjectId || projectIds[0];
        const freshRows = await fetchDofficers({
          force: true,
          projectId: refreshProjectId || undefined,
        });
        if (freshRows.length > 0) setRows(freshRows);
      } catch {
        /* keep optimistic row if list refresh fails */
      }

      setSubmitSuccess(
        typeof created?.message === 'string' && created.message.trim()
          ? created.message
          : createdUser?.username
            ? `Field Officer “${createdUser.username}” created successfully.`
            : 'Field Officer created successfully.',
      );
      setTimeout(() => {
        setShowCreateModal(false);
        resetForm();
      }, 600);
    } catch (err) {
      const friendly = humanizeError(err, 'Could not create the field officer. Please try again.');
      setSubmitError(normalizeUserDuplicateMessage(friendly));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="foState">Loading field officers...</div>;
  if (error) return <div className="foState foError">{error}</div>;

  return (
    <div className="fieldOfficerPage dashboard status-page">
      <header className="dashboard-page-header status-header-row">
        <div>
          <h2 className="dashboard-page-title">Field Officer</h2>
        </div>
        <div className="dashboard-header-toolbar">
          <ClientProjectDropdowns
            clientOptions={scope.clientDropdownOptions}
            projectOptions={scope.projectDropdownOptions}
            selectedClientId={scope.selectedClientId}
            selectedProjectId={scope.selectedProjectId}
            onClientChange={scope.onClientChange}
            onProjectChange={scope.onProjectChange}
            projectDisabled={scope.projectNeedsClientFirst || scope.noProjectsForClient}
            projectPlaceholder={scope.projectPlaceholder}
          />
          <button
            className="foPrimaryBtn foHeaderAddBtn"
            type="button"
            onClick={() => {
              resetForm();
              const defaultProjectIds = [];
              if (scope.selectedProjectId) {
                defaultProjectIds.push(scope.selectedProjectId);
              } else if (projectOptionsForAssign.length === 1) {
                defaultProjectIds.push(projectOptionsForAssign[0].id);
              }
              setForm((prev) => ({
                ...prev,
                project_ids: defaultProjectIds,
              }));
              if (projectOptionsForAssign.length === 0) {
                setSubmitError(
                  'No projects are available to assign. Log in with a user that has project access.',
                );
              }
              setShowCreateModal(true);
            }}
          >
            + Add Field Officer
          </button>
        </div>
      </header>

      <div className="status-toolbar">
        <input
          className="status-search"
          type="search"
          placeholder="Search by Keywords"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          aria-label="Search by Keywords"
        />
        <div className="status-toolbar-right dashboard-header-toolbar">
          <HierarchyFilterDropdowns
            filterByOptions={hierarchy.filterByDropdownOptions}
            locationOptions={hierarchy.locationDropdownOptions}
            filterByField={hierarchy.filterByField}
            locationFilter={hierarchy.locationFilter}
            onFilterByChange={hierarchy.onFilterByChange}
            onLocationChange={hierarchy.setLocationFilter}
            locationPlaceholder={hierarchy.locationPlaceholder}
          />
          <Button type="button" className="export-meter-csv-button" onClick={exportCsv}>
            <img className="export-meter-csv-button-icon" src={exportIcon} alt="" aria-hidden="true" />
            <span className="export-meter-csv-button-label">Export</span>
          </Button>
        </div>
      </div>

      <div className="foTableCard">
        <DataTable
          tableClassName="foTable"
          columns={foColumns}
          rows={pageRows}
          getRowKey={(row) => `${row.id}-${row.name}`}
          emptyMessage="No field officers found"
          emptyCellClassName="foEmpty"
          aria-label="Field officers"
        />

        <div className="foPagination">
          <span className="foTotalCount" aria-live="polite">
            Total: {filteredRows.length}
          </span>
          <div className="foPaginationControls">
            <button
              className="foPageBtn"
              type="button"
              disabled={safePage === 1}
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              aria-label="Previous page"
            >
              <FiChevronLeft />
            </button>
            {pageList.map((entry, idx) => {
              if (entry === 'left-ellipsis' || entry === 'right-ellipsis') {
                return (
                  <span key={`${entry}-${idx}`} className="foPageEllipsis">
                    …
                  </span>
                );
              }
              return (
                <button
                  key={entry}
                  type="button"
                  className={`foPageNumber ${entry === safePage ? 'active' : ''}`}
                  onClick={() => setPage(entry)}
                >
                  {entry}
                </button>
              );
            })}
            <button
              className="foPageBtn"
              type="button"
              disabled={safePage === totalPages}
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              aria-label="Next page"
            >
              <FiChevronRight />
            </button>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <div className="foModalBackdrop" role="presentation" onClick={() => setShowCreateModal(false)}>
          <div className="foModal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="foModalHeader">
              <div>
                <h3>Create Field Officer</h3>
                <p>Add a new officer and assign projects</p>
              </div>
              <button type="button" className="foModalClose" onClick={() => setShowCreateModal(false)}>
                <FiX />
              </button>
            </div>

            <form className="foModalForm" onSubmit={handleCreateFieldOfficer}>
              <label>
                Username
                <input
                  required
                  value={form.username}
                  onChange={(e) => setForm((prev) => ({ ...prev, username: e.target.value }))}
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm((prev) => ({ ...prev, email: e.target.value }))}
                />
              </label>
              <label>
                Password
                <input
                  type="password"
                  required
                  value={form.password}
                  onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                />
              </label>
              <label>
                First Name
                <input
                  required
                  value={form.first_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, first_name: e.target.value }))}
                />
              </label>
              <label>
                Last Name
                <input
                  required
                  value={form.last_name}
                  onChange={(e) => setForm((prev) => ({ ...prev, last_name: e.target.value }))}
                />
              </label>
              <label>
                Phone
                <input
                  required
                  value={form.phone}
                  onChange={(e) => setForm((prev) => ({ ...prev, phone: e.target.value }))}
                />
              </label>
              <div className="foProjectsField foMultiselect" ref={projectPickerRef}>
                <span className="foMultiselectLabel">Projects</span>
                <div className={`foMsShell ${projectMenuOpen ? 'open' : ''}`}>
                  <button
                    type="button"
                    className="foMsTrigger"
                    onClick={() => setProjectMenuOpen((prev) => !prev)}
                    aria-haspopup="listbox"
                    aria-expanded={projectMenuOpen}
                    disabled={projectOptionsForAssign.length === 0}
                  >
                    <span
                      className={`foMsTriggerLabel ${
                        selectedProjectLabel ? '' : 'foMsTriggerLabelPlaceholder'
                      }`}
                    >
                      {selectedProjectLabel ||
                        (projectOptionsForAssign.length === 0
                          ? 'No projects available'
                          : 'Select projects')}
                    </span>
                    <span
                      className={`foMsCaret ${projectMenuOpen ? 'foMsCaretOpen' : ''}`}
                      aria-hidden
                    >
                      <img src={dropdownIcon} alt="" className="foMsCaretIcon" />
                    </span>
                  </button>
                </div>

                {projectMenuOpen ? (
                  <div className="foMsPanel" role="presentation">
                    <div className="foMsSearchWrap">
                      <input
                        type="search"
                        className="foMsSearch"
                        placeholder="Search projects"
                        value={projectSearch}
                        onChange={(e) => setProjectSearch(e.target.value)}
                        autoFocus
                      />
                    </div>
                    <ul
                      className="foMsList"
                      role="listbox"
                      aria-multiselectable="true"
                      aria-label="Projects"
                    >
                      {visibleProjectOptions.length === 0 ? (
                        <li className="foMsEmpty">No matches found</li>
                      ) : (
                        visibleProjectOptions.map((opt) => {
                          const picked = form.project_ids.some(
                            (pid) => String(pid) === String(opt.id),
                          );
                          return (
                            <li key={opt.id} className="foMsItem">
                              <div
                                className={`foMsOptionShell ${picked ? 'foMsOptionSelected' : ''}`}
                              >
                                <button
                                  type="button"
                                  className="foMsOption"
                                  role="option"
                                  aria-selected={picked}
                                  onClick={() => toggleProject(opt.id)}
                                >
                                  {opt.label}
                                </button>
                              </div>
                            </li>
                          );
                        })
                      )}
                    </ul>
                    {form.project_ids.length > 0 ? (
                      <div className="foMsFooter">
                        <span className="foMsCount">
                          {form.project_ids.length} selected
                        </span>
                        <button
                          type="button"
                          className="foMsClear"
                          onClick={clearProjects}
                        >
                          Clear
                        </button>
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>

              {submitError ? <p className="foSubmitError">{submitError}</p> : null}
              {submitSuccess ? <p className="foSubmitSuccess">{submitSuccess}</p> : null}

              <div className="foModalActions">
                <button type="button" className="foChipBtn" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="foPrimaryBtn" disabled={submitting}>
                  {submitting ? 'Creating...' : 'Create Field Officer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
